import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon, ArrowDownIcon, MapPinIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
interface HeroProps {
  onNavigate?: (page: string) => void;
}
function AnimatedCounter({
  target,
  suffix = ''
}: {
  target: number;
  suffix?: string;
}) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => Math.round(v));
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(count, target, {
      duration: 2.5,
      delay: 1.2,
      ease: [0.16, 1, 0.3, 1]
    });
    const unsubscribe = rounded.on('change', v => setDisplay(v));
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [target]);
  return <span data-id="element-139">
      {display}
      {suffix}
    </span>;
}
export function Hero({
  onNavigate
}: HeroProps) {
  const handleNavClick = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative min-h-screen min-h-[svh] bg-gesl-dark overflow-hidden" data-id="element-140">
      {/* Full-Screen Background Image */}
      <motion.div initial={{
      scale: 1.1,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 2,
      ease: [0.16, 1, 0.3, 1]
    }} className="absolute inset-0" data-id="element-141">
        <img src="https://picsum.photos/seed/gesl-aerial-green/1920/1080" alt="Aerial view of Nigerian landscape" className="w-full h-full object-cover" data-id="element-142" />
      </motion.div>

      {/* Layered Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-gesl-dark via-gesl-dark/70 to-gesl-dark/30" data-id="element-143" />
      <div className="absolute inset-0 bg-gradient-to-r from-gesl-dark/80 via-transparent to-transparent" data-id="element-144" />

      {/* Vertical Side Accent */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} className="absolute left-6 lg:left-10 top-0 bottom-0 hidden md:flex flex-col items-center justify-center z-20" data-id="element-145">
        <div className="h-16 w-px bg-gradient-to-b from-transparent via-gesl-gold to-transparent mb-4" data-id="element-146" />
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/20 [writing-mode:vertical-lr] rotate-180" data-id="element-147">
          Greenwater Environmental
        </span>
        <div className="h-16 w-px bg-gradient-to-b from-transparent via-gesl-gold to-transparent mt-4" data-id="element-148" />
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 h-full min-h-screen min-h-[svh] flex flex-col justify-end pt-28 md:pt-32 pb-6 lg:pb-0" data-id="element-149">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-150">
          {/* Hero Text Block */}
          <div className="mb-6 lg:mb-8 md:pl-12 lg:pl-16" data-id="element-151">
            {/* Eyebrow */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="flex items-center gap-3 mb-4 sm:mb-5" data-id="element-152">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: 48
            }} transition={{
              duration: 0.8,
              delay: 0.8
            }} className="h-[2px] bg-gesl-gold hidden sm:block" data-id="element-153" />
              <span className="text-gesl-gold text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase" data-id="element-154">
                Est. 2000 — Abuja, Nigeria
              </span>
            </motion.div>

            {/* Main Headline */}
            <div className="overflow-hidden mb-0.5 sm:mb-1" data-id="element-155">
              <motion.h1 initial={{
              y: '100%'
            }} animate={{
              y: 0
            }} transition={{
              duration: 1,
              delay: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="text-[2.5rem] sm:text-5xl md:text-[3.5rem] lg:text-7xl font-bold text-white leading-[0.95] tracking-tight" data-id="element-156">
                Protecting
              </motion.h1>
            </div>
            <div className="overflow-hidden mb-0.5 sm:mb-1" data-id="element-157">
              <motion.h1 initial={{
              y: '100%'
            }} animate={{
              y: 0
            }} transition={{
              duration: 1,
              delay: 0.75,
              ease: [0.16, 1, 0.3, 1]
            }} className="text-[2.5rem] sm:text-5xl md:text-[3.5rem] lg:text-7xl font-bold leading-[0.95] tracking-tight" data-id="element-158">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gesl-gold via-gesl-gold to-white/60" data-id="element-159">
                  Tomorrow's
                </span>
              </motion.h1>
            </div>
            <div className="overflow-hidden mb-5 sm:mb-6" data-id="element-160">
              <motion.h1 initial={{
              y: '100%'
            }} animate={{
              y: 0
            }} transition={{
              duration: 1,
              delay: 0.9,
              ease: [0.16, 1, 0.3, 1]
            }} className="text-[2.5rem] sm:text-5xl md:text-[3.5rem] lg:text-7xl font-bold text-white leading-[0.95] tracking-tight" data-id="element-161">
                Environment
              </motion.h1>
            </div>

            {/* Subtitle + CTA Row */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.2
          }} className="flex flex-col lg:flex-row lg:items-end gap-5 lg:gap-12" data-id="element-162">
              <p className="text-white/50 text-sm max-w-sm leading-relaxed font-light" data-id="element-163">
                Nigeria's premier environmental management consultancy
                delivering scientific solutions to complex challenges across
                energy, infrastructure, and development sectors.
              </p>
              <div className="flex items-center gap-3" data-id="element-164">
                <button onClick={() => handleNavClick('services')} className="group flex items-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 bg-gesl-gold text-white text-sm font-semibold rounded-full hover:bg-white hover:text-gesl-dark transition-all duration-500 shadow-lg shadow-gesl-gold/20" data-id="element-165">
                  Our Services
                  <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-166" />
                </button>
                <button onClick={() => handleNavClick('contact')} className="flex items-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 text-white text-sm font-semibold rounded-full border border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300" data-id="element-167">
                  Contact
                </button>
              </div>
            </motion.div>
          </div>

          {/* Bottom Feature Cards — hidden on mobile, visible sm+ */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.5
        }} className="hidden sm:grid sm:grid-cols-3 gap-px bg-white/10 rounded-lg overflow-hidden backdrop-blur-sm border border-white/10 md:ml-12 lg:ml-16" data-id="element-168">
            {[{
            icon: ShieldCheckIcon,
            number: 30,
            suffix: '+',
            label: 'Years of Excellence',
            detail: 'CAC Registered • NESREA Accredited'
          }, {
            icon: MapPinIcon,
            number: 200,
            suffix: '+',
            label: 'Projects Delivered',
            detail: 'Across all geopolitical zones'
          }, {
            icon: UsersIcon,
            number: 9,
            suffix: '+',
            label: 'Industries Served',
            detail: 'Oil & Gas, Power, Aviation & more'
          }].map((card, idx) => <div key={idx} className="bg-gesl-dark/60 backdrop-blur-xl p-5 lg:p-6 group hover:bg-gesl-dark/80 transition-colors duration-500" data-id="element-169">
                <div className="flex items-start justify-between mb-3" data-id="element-170">
                  <card.icon className="h-4 w-4 text-gesl-gold" data-id="element-171" />
                  <span className="text-2xl lg:text-3xl font-bold text-white tracking-tight" data-id="element-172">
                    <AnimatedCounter target={card.number} suffix={card.suffix} data-id="element-173" />
                  </span>
                </div>
                <h3 className="text-white font-semibold text-xs mb-0.5" data-id="element-174">
                  {card.label}
                </h3>
                <p className="text-white/40 text-[11px] leading-relaxed" data-id="element-175">
                  {card.detail}
                </p>
              </div>)}
          </motion.div>

          {/* Trusted By + Scroll — Bottom Strip — hidden on mobile */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 2,
          duration: 1
        }} className="hidden sm:flex items-center justify-between mt-4 pb-2 md:pl-12 lg:pl-16" data-id="element-176">
            <div className="flex items-center gap-4 overflow-hidden" data-id="element-177">
              <span className="text-[10px] text-white/20 uppercase tracking-[0.2em] font-bold whitespace-nowrap" data-id="element-178">
                Trusted by
              </span>
              <div className="flex items-center gap-3" data-id="element-179">
                {['Federal Govt.', 'World Bank', 'AfDB', 'Chevron', 'Dangote'].map((name, i) => <motion.span key={i} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 2.2 + i * 0.1
              }} className="text-white/20 text-xs font-semibold tracking-tight whitespace-nowrap" data-id="element-180">
                    {name}
                    {i < 4 && <span className="text-white/10 ml-3" data-id="element-181">·</span>}
                  </motion.span>)}
              </div>
            </div>

            <motion.button onClick={() => window.scrollBy({
            top: window.innerHeight * 0.85,
            behavior: 'smooth'
          })} className="flex items-center gap-2 text-white/20 hover:text-white/50 transition-colors" animate={{
            y: [0, 4, 0]
          }} transition={{
            repeat: Infinity,
            duration: 2.5,
            ease: 'easeInOut'
          }} data-id="element-182">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold" data-id="element-183">
                Scroll
              </span>
              <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-184" />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>;
}